import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import '../styles/ResumeRequestModal.css';

export default function ResumeRequestModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        name: '', email: '', company: '', position: '', message: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error' | null

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        const handler = e => { if (e.key === 'Escape') onClose(); };
        if (isOpen) window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSubmitting(true);
        setStatus(null);

        try {
            await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                {
                    from_name:       formData.name,
                    from_email:      formData.email,
                    company:         formData.company  || 'Not specified',
                    position:        formData.position || 'Not specified',
                    message:         formData.message  || 'No additional message',
                    to_name:         'Samwel Waithaka',
                    reply_to:        formData.email,
                    submission_time: new Date().toLocaleString(),
                },
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            );
            setStatus('success');
            setTimeout(() => {
                onClose();
                setFormData({ name: '', email: '', company: '', position: '', message: '' });
                setStatus(null);
            }, 2200);
        } catch (err) {
            console.error(err);
            setStatus('error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="rrm-overlay" onClick={onClose} role="dialog" aria-modal="true">
            <div className="rrm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="rrm-header">
                    <div>
                        <p className="rrm-eyebrow">// CV request</p>
                        <h2 className="rrm-title">Request Resume</h2>
                    </div>
                    <button className="rrm-close" onClick={onClose} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                {/* Success state */}
                {status === 'success' ? (
                    <div className="rrm-success">
                        <div className="rrm-success-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                        </div>
                        <h3>Request Sent!</h3>
                        <p>Thanks for reaching out. I'll send my CV within 24 hours.</p>
                    </div>
                ) : (
                    <form className="rrm-form" onSubmit={handleSubmit}>
                        <div className="rrm-grid">
                            <div className="rrm-field">
                                <label htmlFor="rrm-name">Full Name <span className="req">*</span></label>
                                <input
                                    id="rrm-name" name="name" type="text"
                                    value={formData.name} onChange={handleChange}
                                    placeholder="Your full name" required disabled={submitting}
                                />
                            </div>
                            <div className="rrm-field">
                                <label htmlFor="rrm-email">Email <span className="req">*</span></label>
                                <input
                                    id="rrm-email" name="email" type="email"
                                    value={formData.email} onChange={handleChange}
                                    placeholder="your@email.com" required disabled={submitting}
                                />
                            </div>
                            <div className="rrm-field">
                                <label htmlFor="rrm-company">Company</label>
                                <input
                                    id="rrm-company" name="company" type="text"
                                    value={formData.company} onChange={handleChange}
                                    placeholder="Company / organization" disabled={submitting}
                                />
                            </div>
                            <div className="rrm-field">
                                <label htmlFor="rrm-position">Role</label>
                                <input
                                    id="rrm-position" name="position" type="text"
                                    value={formData.position} onChange={handleChange}
                                    placeholder="Recruiter, Hiring Manager…" disabled={submitting}
                                />
                            </div>
                        </div>

                        <div className="rrm-field">
                            <label htmlFor="rrm-message">Message <span className="optional">(optional)</span></label>
                            <textarea
                                id="rrm-message" name="message"
                                value={formData.message} onChange={handleChange}
                                rows={4} placeholder="Tell me about the opportunity…"
                                disabled={submitting}
                            />
                        </div>

                        {status === 'error' && (
                            <div className="rrm-error">
                                Failed to send. Try again or email me at{' '}
                                <a href="mailto:dev@waithakasam.com">dev@waithakasam.com</a>
                            </div>
                        )}

                        <div className="rrm-actions">
                            <button
                                type="button"
                                className="btn btn-color-2"
                                onClick={onClose}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-color-1"
                                disabled={submitting}
                            >
                                {submitting ? 'Sending…' : 'Send Request →'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}